import { Component, OnInit, inject, signal } from '@angular/core';
import { FieldType, FieldTypeConfig, FormlyModule } from '@ngx-formly/core';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { VendorsService } from '@app/services/vendors.service/vendors.service';

interface SelectedFile {
  file: File;
  index: number;
}

interface SubmittedFile {
  path: string;
  index: number;
}

@Component({
  standalone: true,
  selector: 'formly-file-upload-multi',
  templateUrl: './file-upload-multi.type.html',
  imports: [CommonModule, FormlyModule, MatButtonModule, MatIconModule, MatProgressSpinnerModule],
})
export class FileUploadMultiComponent extends FieldType<FieldTypeConfig> implements OnInit {
  private vendorsService = inject(VendorsService);

  private readonly MAX_FILE_SIZE = 10 * 1024 * 1024; // 10 MB
  private readonly MAX_FILES = 10;

  selectedFiles: SelectedFile[] = [];
  fileSizeErrors = new Map<number, string>();
  downloadingIndex = signal<number | null>(null);

  get isReadonly(): boolean {
    return this.formState?.readonly === true;
  }

  get vendorId(): number | null {
    return this.formState?.vendorId || null;
  }

  get maxFiles(): number {
    return this.props['maxFiles'] || this.MAX_FILES;
  }

  get maxFileSize(): number {
    return this.props['maxFileSizeBytes'] || this.MAX_FILE_SIZE;
  }

  get submittedFiles(): SubmittedFile[] {
    // In readonly mode, read from formControl.value which should be the list of S3 paths
    const value = this.formControl.value;
    if (!value || !Array.isArray(value)) {
      return [];
    }
    return value
      .map((path: string, index: number) => ({ path, index }))
      .filter((f: SubmittedFile) => f.path && typeof f.path === 'string' && !f.path.startsWith('file:'));
  }

  get canAddMoreFiles(): boolean {
    return this.selectedFiles.length < this.maxFiles;
  }

  ngOnInit(): void {
    // Restore previously selected files from formState.files
    if (this.formState?.files && this.key) {
      for (let i = 0; i < this.maxFiles; i++) {
        const fileKey = `${this.key as string}_${i}`;
        const existingFile = this.formState.files[fileKey];
        if (existingFile instanceof File) {
          this.selectedFiles.push({ file: existingFile, index: i });
        }
      }
      this.updateFormControlValue();
    }
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (!input.files || input.files.length === 0) {
      return;
    }

    const file = input.files[0];

    // Check if we can add more files
    if (this.selectedFiles.length >= this.maxFiles) {
      input.value = '';
      return;
    }

    // Check file size
    if (file.size > this.maxFileSize) {
      const nextIndex = this.getNextAvailableIndex();
      this.fileSizeErrors.set(nextIndex, `File exceeds maximum size of 10 MB (${this.formatFileSize(file.size)})`);
      input.value = '';
      return;
    }

    // Find next available index
    const index = this.getNextAvailableIndex();
    this.fileSizeErrors.delete(index);

    // Add to selected files
    this.selectedFiles.push({ file, index });

    // Store in formState.files
    if (!this.formState.files) {
      this.formState.files = {};
    }
    const fileKey = `${this.key as string}_${index}`;
    this.formState.files[fileKey] = file;

    // Update form control value
    this.updateFormControlValue();

    // Clear input for next selection
    input.value = '';
  }

  removeFile(index: number): void {
    // Remove from selectedFiles
    this.selectedFiles = this.selectedFiles.filter((f) => f.index !== index);

    // Remove from formState.files
    if (this.formState?.files) {
      const fileKey = `${this.key as string}_${index}`;
      delete this.formState.files[fileKey];
    }

    // Clear any error for this index
    this.fileSizeErrors.delete(index);

    // Update form control value
    this.updateFormControlValue();
  }

  private getNextAvailableIndex(): number {
    const usedIndices = new Set(this.selectedFiles.map((f) => f.index));
    for (let i = 0; i < this.maxFiles; i++) {
      if (!usedIndices.has(i)) {
        return i;
      }
    }
    return this.selectedFiles.length;
  }

  private updateFormControlValue(): void {
    if (this.selectedFiles.length === 0) {
      this.formControl.setValue(null);
    } else {
      // Set placeholder values for validation
      const placeholders = this.selectedFiles.map((f) => `file:${f.file.name}`);
      this.formControl.setValue(placeholders);
    }
    this.formControl.markAsTouched();
  }

  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  getFileName(path: string): string {
    if (!path) return '';
    const parts = path.split('/');
    const filename = parts[parts.length - 1];
    // Remove timestamp prefix if present (format: fieldname-YYYYMMDDHHMMSS-filename)
    const match = filename.match(/^[^-]+-\d{14}-(.+)$/);
    return match ? match[1] : filename;
  }

  downloadSubmittedFile(index: number): void {
    if (!this.vendorId || !this.key) {
      console.error('Cannot download: vendorId or field key is missing');
      return;
    }

    this.downloadingIndex.set(index);
    const fieldName = `${this.key as string}_${index}`;

    this.vendorsService.getVendorFileDownloadUrl(this.vendorId, fieldName).subscribe({
      next: (url) => {
        const link = document.createElement('a');
        link.href = url;
        const submittedFile = this.submittedFiles.find((f) => f.index === index);
        link.download = submittedFile ? this.getFileName(submittedFile.path) : 'download';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        this.downloadingIndex.set(null);
      },
      error: (err) => {
        console.error('Failed to get download URL:', err);
        this.downloadingIndex.set(null);
      },
    });
  }
}
